import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import dayjs from 'dayjs';
import { graphql } from '../../gql';
import { Slideout } from '../slideout';
import { useNotifications } from '../../notifications';
import { ReactComponent as ChecklistIcon } from '../../shared/icons/checklist-icon.svg';
import { ReactComponent as ChevronDownIcon } from '../../shared/icons/chevron-down.svg';
import { ReactComponent as ChevronRightIcon } from '../../shared/icons/chevron-right.svg';
import { ReactComponent as BackIcon } from '../../shared/icons/chevron-left.svg';
import { ReactComponent as PlusIcon } from '../../shared/icons/plus-icon.svg';
import { AddTemplateItemsForm } from './AddTemplateItemsForm';
import { AddItemForm } from './AddItemForm';
import { EditItemForm } from './EditItemForm';
import { useTeamInfo } from '../../team-id-context';
import styles from './ChecklistSlideout.module.scss';

const GET_CHECKLISTS_BY_DEAL = graphql(/* GraphQL */ `
  query GetChecklistsByDealForSlideout($dealId: ID!) {
    checklistsByDeal(dealId: $dealId) {
      id
      uuid
      name
      completedCount
      totalCount
      createdAt
      creator {
        id
        fullName
        email
      }
      items {
        id
        content
        completed
        completedAt
        displayOrder
        dueDate
        reminderDaysBefore
        autoCompleteOnSignature
        completedBy {
          id
          fullName
        }
        assignedTo {
          id
          fullName
        }
        document {
          id
          title
        }
        children {
          id
          content
          completed
          completedAt
          displayOrder
          dueDate
          reminderDaysBefore
          autoCompleteOnSignature
          completedBy {
            id
            fullName
          }
          assignedTo {
            id
            fullName
          }
          document {
            id
            title
          }
        }
      }
    }
  }
`);

const UPDATE_CHECKLIST_ITEM = graphql(/* GraphQL */ `
  mutation UpdateChecklistItemInSlideout(
    $id: ID!
    $content: String
    $completed: Boolean
    $dueDate: ISO8601Date
    $reminderDaysBefore: Int
    $documentId: ID
    $assignedToId: ID
    $autoCompleteOnSignature: Boolean
    $displayOrder: Int
    $parentItemId: ID
  ) {
    updateChecklistItem(
      id: $id
      content: $content
      completed: $completed
      dueDate: $dueDate
      reminderDaysBefore: $reminderDaysBefore
      documentId: $documentId
      assignedToId: $assignedToId
      autoCompleteOnSignature: $autoCompleteOnSignature
      displayOrder: $displayOrder
      parentItemId: $parentItemId
    ) {
      dealChecklistItem {
        id
        content
        completed
        completedAt
        completedBy {
          id
          fullName
        }
      }
      errors
    }
  }
`);

interface ChecklistSlideoutProps {
  dealId: string;
  isOpen: boolean;
  onClose: () => void;
}

interface ChecklistItem {
  id: string;
  content: string;
  completed: boolean;
  completedAt?: string | null;
  displayOrder: number;
  dueDate?: string | null;
  reminderDaysBefore?: number | null;
  autoCompleteOnSignature?: boolean | null;
  completedBy?: {
    id: string;
    fullName: string;
  } | null;
  assignedTo?: {
    id: string;
    fullName: string;
  } | null;
  document?: {
    id: string;
    title: string;
  } | null;
  children?: ChecklistItem[];
}

interface Checklist {
  id: string;
  uuid: string;
  name: string;
  completedCount: number;
  totalCount: number;
  createdAt: string;
  creator: {
    id: string;
    fullName: string;
    email: string;
  };
  items?: ChecklistItem[];
}

type ViewType = 'list' | 'add-templates' | 'add-item' | 'edit-item';

export const ChecklistSlideout: React.FC<ChecklistSlideoutProps> = ({ dealId, isOpen, onClose }) => {
  const { teamId } = useTeamInfo();
  const [, { error: notifyError }] = useNotifications();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [currentView, setCurrentView] = useState<ViewType>('list');
  const [editingItem, setEditingItem] = useState<ChecklistItem | null>(null);

  // Fetch checklists
  const { data, loading, refetch } = useQuery(GET_CHECKLISTS_BY_DEAL, {
    variables: { dealId },
    skip: !isOpen || !dealId,
    fetchPolicy: 'cache-and-network',
  });

  // Mutations
  const [updateItem] = useMutation(UPDATE_CHECKLIST_ITEM);

  const checklists: Checklist[] = data?.checklistsByDeal || [];
  const checklist = checklists[0]; // Single checklist per deal

  // Toggle item expansion (for nested items)
  const toggleItemExpanded = (itemId: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  // Toggle item completion
  const handleToggleItem = async (itemId: string, currentCompleted: boolean) => {
    try {
      await updateItem({
        variables: {
          id: itemId,
          completed: !currentCompleted,
        },
      });
      await refetch();
    } catch (error) {
      notifyError({
        message: {
          id: 'checklist.item.update.error',
          defaultMessage: 'Failed to update item',
        },
      });
    }
  };

  // Handle item click for editing
  const handleItemClick = (item: ChecklistItem) => {
    setEditingItem(item);
    setCurrentView('edit-item');
  };

  // Render a single checklist item
  const renderItem = (item: ChecklistItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.has(item.id);
    const isOverdue = item.dueDate && !item.completed && dayjs(item.dueDate).isBefore(dayjs(), 'day');

    return (
      <div key={item.id} className={styles.checklistItem} style={{ marginLeft: `${level * 32}px` }}>
        <div className={styles.checklistItem__content} onClick={() => handleItemClick(item)}>
          {/* Checkbox */}
          <input
            type="checkbox"
            checked={item.completed}
            onChange={(e) => {
              e.stopPropagation();
              handleToggleItem(item.id, item.completed);
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            className={styles.checklistItem__checkbox}
            disabled={loading}
          />

          {/* Expand/collapse button for nested items */}
          {hasChildren && (
            <button
              className={styles.checklistItem__expandButton}
              onClick={(e) => {
                e.stopPropagation();
                toggleItemExpanded(item.id);
              }}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
            </button>
          )}

          {/* Item text and details */}
          <div className={styles.checklistItem__details}>
            {/* Title row with due date pill or completed by text */}
            <div className={styles.checklistItem__titleRow}>
              <span className={`${styles.checklistItem__text} ${item.completed ? styles.checklistItem__text__completed : ''}`}>
                {item.content}
              </span>
              {item.dueDate ? (
                <span className={`${styles.checklistItem__dueDatePill} ${isOverdue ? styles.checklistItem__dueDatePill__overdue : ''}`}>
                  {dayjs(item.dueDate).format('MMM D')}
                </span>
              ) : (
                item.completedBy && item.completedAt && (
                  <span className={styles.checklistItem__completedByInline}>
                    <FormattedMessage
                      id="checklist.completed.by.inline"
                      defaultMessage="Completed by {name}"
                      values={{
                        name: item.completedBy.fullName,
                      }}
                    />
                  </span>
                )
              )}
            </div>

            {/* Metadata row: Linked document (left) and Assigned to (right) */}
            {(item.document || item.assignedTo) && (
              <div className={styles.checklistItem__metadata}>
                <div className={styles.checklistItem__metadataLeft}>
                  {item.document && (
                    <span className={styles.checklistItem__linkedDoc}>
                      <FormattedMessage
                        id="checklist.linked"
                        defaultMessage="Linked: {doc}"
                        values={{ doc: item.document.title }}
                      />
                    </span>
                  )}
                </div>
                <div className={styles.checklistItem__metadataRight}>
                  {item.assignedTo && !item.completed && (
                    <span className={styles.checklistItem__assignee}>
                      <FormattedMessage
                        id="checklist.assigned"
                        defaultMessage="Assigned to: {name}"
                        values={{ name: item.assignedTo.fullName }}
                      />
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Render nested children if expanded */}
        {hasChildren && isExpanded && (
          <div className={styles.checklistItem__children}>
            {item.children!.map((child) => renderItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Render the appropriate view
  const renderView = () => {
    if (currentView === 'add-templates') {
      return (
        <AddTemplateItemsForm
          dealId={dealId}
          teamId={teamId}
          checklists={checklists}
          onSuccess={() => {
            setCurrentView('list');
          }}
          onCancel={() => setCurrentView('list')}
          refetch={refetch}
        />
      );
    }

    if (currentView === 'add-item') {
      return (
        <AddItemForm
          dealId={dealId}
          checklists={checklists}
          onSuccess={() => {
            setCurrentView('list');
          }}
          onCancel={() => setCurrentView('list')}
          refetch={refetch}
        />
      );
    }

    if (currentView === 'edit-item' && editingItem) {
      return (
        <EditItemForm
          dealId={dealId}
          item={editingItem}
          onSuccess={() => {
            setEditingItem(null);
            setCurrentView('list');
          }}
          onCancel={() => {
            setEditingItem(null);
            setCurrentView('list');
          }}
          refetch={refetch}
        />
      );
    }

    // List view
    return (
      <>
        {/* Action Buttons */}
        <div className={styles.actionButtons}>
          <button className={styles.actionButtons__addItem} onClick={() => setCurrentView('add-item')} disabled={loading}>
            <PlusIcon className={styles.actionButtons__icon} />
            <FormattedMessage id="checklist.add.item" defaultMessage="Add Item" />
          </button>
          <button className={styles.actionButtons__addChecklist} onClick={() => setCurrentView('add-templates')} disabled={loading}>
            <ChecklistIcon className={styles.actionButtons__icon} />
            <FormattedMessage id="checklist.add.templates" defaultMessage="Add Templates" />
          </button>
        </div>

        {/* Checklist Items */}
        {loading && checklists.length === 0 ? (
          <div className={styles.loadingState}>
            <FormattedMessage id="checklist.loading" defaultMessage="Loading..." />
          </div>
        ) : !checklist || checklist.items.length === 0 ? (
          <div className={styles.emptyState}>
            <ChecklistIcon className={styles.emptyState__icon} />
            <h3 className={styles.emptyState__title}>
              <FormattedMessage id="checklist.empty.title" defaultMessage="No checklist items yet" />
            </h3>
            <p className={styles.emptyState__description}>
              <FormattedMessage
                id="checklist.empty.description"
                defaultMessage="Add items manually or from templates to start tracking tasks for this deal."
              />
            </p>
          </div>
        ) : (
          <div className={styles.checklistItems}>{checklist.items.map((item) => renderItem(item))}</div>
        )}
      </>
    );
  };

  // Custom title with navigation
  const renderTitle = () => {
    const progressPercentage =
      checklist && checklist.totalCount > 0 ? Math.round((checklist.completedCount / checklist.totalCount) * 100) : 0;
    const showProgress = currentView === 'list' && checklist && checklist.totalCount > 0;

    return (
      <div className={styles.slideoutHeader}>
        {(currentView === 'add-templates' || currentView === 'add-item' || currentView === 'edit-item') && (
          <button
            className={styles.slideoutHeader__backButton}
            onClick={() => {
              if (currentView === 'edit-item') {
                setEditingItem(null);
              }
              setCurrentView('list');
            }}
            aria-label="Back to checklist"
          >
            <BackIcon />
          </button>
        )}
        <span className={styles.slideoutHeader__title}>
          {currentView === 'add-templates' ? (
            <FormattedMessage id="checklist.add.templates.title" defaultMessage="Add from Templates" />
          ) : currentView === 'add-item' ? (
            <FormattedMessage id="checklist.add.item.title" defaultMessage="Add Item" />
          ) : currentView === 'edit-item' ? (
            <FormattedMessage id="checklist.edit.item.title" defaultMessage="Edit Item" />
          ) : (
            <FormattedMessage id="checklist.slideout.title" defaultMessage="Checklist" />
          )}
        </span>
        {showProgress && (
          <div className={styles.slideoutHeader__progress}>
            <span className={styles.slideoutHeader__progressText}>
              {checklist.completedCount} / {checklist.totalCount} <FormattedMessage id="checklist.completed" defaultMessage="completed" />
            </span>
            <div className={styles.slideoutHeader__progressBar}>
              <div
                className={styles.slideoutHeader__progressFill}
                style={{ width: `${progressPercentage}%` }}
                role="progressbar"
                aria-valuenow={progressPercentage}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Slideout isOpen={isOpen} onClose={onClose} title={renderTitle()} width="medium">
      <div className={styles.slideoutContent}>{renderView()}</div>
    </Slideout>
  );
};

export default ChecklistSlideout;
